import type { NotificationView, ScreenFilterDef, ScreenStat } from '@taki/ui';

/**
 * LA PORTE « NOTIFICATIONS » DU JOURNAL, SANS REACT : les mots, l'origine, le
 * statut affiche, le catalogue de filtres et les stats.
 *
 * Une ligne par ENVOI (un document `orgs/{orgId}/notifications/{id}`,
 * `docs/NOTIFICATIONS.md`) : quand, a qui, par quel canal, a quel propos
 * (rubrique), d'ou il vient (origine, avec son lien), ou il en est (statut) et
 * pourquoi il a echoue. Teste sans DOM (`packages/shell/tests/journal-notifications.test.mjs`).
 */

/** Un envoi tel que la porte le montre : la vue du port, marquee pour la distinguer d'un evenement. */
export type EnvoiJournal = NotificationView & { genre: 'notification' };

/** Le statut AFFICHE : ceux du moteur, plus `simule` (canal console, rien n'est parti). */
export type StatutAffiche = NotificationView['statut'] | 'simule';

export const STATUTS_ENVOI: readonly { id: StatutAffiche; label: string; ton: 'neutral' | 'accent' | 'success' | 'danger' }[] = [
  { id: 'enFile', label: 'En file', ton: 'neutral' },
  { id: 'envoye', label: 'Envoyé', ton: 'success' },
  { id: 'livre', label: 'Livré', ton: 'success' },
  { id: 'simule', label: 'Simulé, non envoyé', ton: 'accent' },
  { id: 'echec', label: 'Échec', ton: 'danger' },
  { id: 'abandonne', label: 'Abandonné', ton: 'danger' },
];

const CANAUX: Readonly<Record<string, string>> = {
  email: 'E-mail', whatsapp: 'WhatsApp', interne: 'Interne', slack: 'Slack', console: 'E-mail',
};

const RUBRIQUES: Readonly<Record<string, string>> = {
  citation: 'Citation', lia: 'Lia', message: 'Message', regle: 'Règle',
};

/**
 * Le statut a montrer. Un message livre par le canal console est SIMULE, quel
 * que soit le statut que le moteur lui donne (`envoye`) : dire « envoye »
 * ferait croire qu'un vrai mail est parti.
 */
export function statutAffiche(n: NotificationView): StatutAffiche {
  return n.simule === true && (n.statut === 'envoye' || n.statut === 'livre') ? 'simule' : n.statut;
}

export function libelleDeStatut(statut: StatutAffiche): string {
  return STATUTS_ENVOI.find((s) => s.id === statut)?.label ?? statut;
}

/** Le canal, en francais. Un e-mail simule reste un e-mail : c'est le statut qui dit « simule ». */
export function libelleDeCanal(canal: string): string {
  return CANAUX[canal] ?? canal;
}

/** La rubrique : celle du message, sinon deduite de sa source (une regle, un essai). */
export function rubriqueDe(n: NotificationView): string {
  if (n.rubrique) return n.rubrique;
  if (n.source.genre === 'regle') return 'regle';
  return 'message';
}

export function libelleDeRubrique(rubrique: string): string {
  return RUBRIQUES[rubrique] ?? rubrique;
}

/** D'ou vient l'envoi : un libelle, et le chemin ou il mene (vide s'il ne mene nulle part). */
export interface OrigineEnvoi {
  libelle: string;
  chemin: string;
}

const LIEUX: Readonly<Record<string, string>> = { canal: 'Canal', direct: 'Message direct', chatter: 'Fiche', page: 'Page' };

/**
 * L'ORIGINE d'un envoi : la conversation d'une mention (canal, page, fiche,
 * message direct), la regle qui l'a produit, la fiche qu'il concerne, ou un
 * essai. Le lien d'une regle mene a l'onglet Notifications des Parametres, ou
 * vivent les regles ; celui d'un message, au chemin qu'il porte.
 */
/**
 * LE TITRE D'UN SUJET DE LIA tel que la notification le porte (« Reponse
 * prete : Quelles societes avons-nous », `functions/src/notifications/lia.ts`),
 * le repli quand le sujet n'est pas lisible ici (celui d'une autre personne).
 */
export function titreDeSujetDansLeSujet(sujet: string): string {
  return sujet.replace(/^R[ée]ponse pr[êe]te\s*:\s*/iu, '').trim();
}

export function origineDe(
  n: NotificationView,
  nomDeRegle: (id: string) => string | undefined = () => undefined,
  /** Le titre COURANT d'un sujet de Lia par son `conversationId` (renomme depuis, il gagne). */
  titreDeSujetLia: (conversationId: string) => string | undefined = () => undefined,
): OrigineEnvoi {
  const chemin = n.chemin ?? '';
  if (n.contexte) {
    const genre = LIEUX[n.contexte.type] ?? 'Conversation';
    const lieu = n.contexte.lieu.trim();
    return { libelle: lieu ? (lieu.startsWith(genre) ? lieu : `${genre} ${lieu}`) : genre, chemin };
  }
  if (n.source.genre === 'regle') {
    const id = n.source.ruleId ?? '';
    return { libelle: `Règle ${nomDeRegle(id) ?? id}`.trim(), chemin: '/settings?t=notifications' };
  }
  if (n.source.genre === 'essai') return { libelle: 'Essai', chemin: '/settings?t=notifications' };
  /* UNE REPONSE DE LIA dans un sujet : son titre, jamais l'identifiant technique
     (ecart 3 de l'essai du soir) ; le lien ouvre ce sujet dans la fenetre. */
  if (n.objet?.type === 'lia') {
    const titre = titreDeSujetLia(n.objet.id) ?? titreDeSujetDansLeSujet(n.sujet);
    return { libelle: titre ? `Lia · ${titre}` : 'Lia', chemin };
  }
  /* UNE CONVERSATION sans contexte (notification d'avant le contexte des
     mentions) : le lieu que son sujet nomme (« ... dans #general »), jamais
     l'identifiant technique. */
  if (n.objet?.type === 'conversation') {
    const lieu = /\bdans\s+(.+)$/u.exec(n.sujet)?.[1]?.trim();
    return { libelle: lieu ? (lieu.startsWith('#') ? `Canal ${lieu}` : lieu) : 'Conversation', chemin };
  }
  if (n.objet) return { libelle: `Fiche ${n.objet.type} / ${n.objet.id}`, chemin };
  if (n.source.famille && n.source.recordId) return { libelle: `Fiche ${n.source.famille} / ${n.source.recordId}`, chemin };
  return { libelle: 'Message', chemin };
}

/** Le destinataire lisible : un nom de l'annuaire, sinon l'adresse. */
export function destinataireDe(n: NotificationView, nommer: (cle: string) => string | undefined = () => undefined): string {
  const email = n.destinataireEmail ?? null;
  return (email ? nommer(email) : undefined) ?? nommer(n.destinataire) ?? email ?? n.destinataire;
}

/** La phrase d'erreur, ou ce qui tient lieu d'explication pour un message simule. */
export function phraseDe(n: NotificationView): string | null {
  if (statutAffiche(n) === 'simule') return 'Simulé : écrit dans la boîte d’envoi locale (.taki-tmp/mails), aucun e-mail n’est parti.';
  return n.derniereErreur ?? null;
}

/** Un envoi peut-il etre rejoue par le socle ? Seul un `echec` l'est (le moteur abandonne le reste). */
export function estRejouable(n: NotificationView): boolean {
  return n.statut === 'echec';
}

/**
 * LE CATALOGUE DE FILTRES de la porte, tire des envois charges : les echecs
 * (echec et abandon), chaque statut present, chaque canal present, chaque
 * destinataire present. Combines en OU, comme sur tous les ecrans natifs.
 */
export function filtresDesEnvois(
  envois: readonly EnvoiJournal[],
  nommer: (cle: string) => string | undefined = () => undefined,
): ScreenFilterDef<EnvoiJournal>[] {
  const statuts = new Set<StatutAffiche>();
  const canaux = new Set<string>();
  const destinataires = new Map<string, string>();
  for (const n of envois) {
    statuts.add(statutAffiche(n));
    canaux.add(n.canal);
    // UNE PERSONNE, UN FILTRE : le meme destinataire porte un email pour
    // l'e-mail et un uid pour WhatsApp ; on regroupe par le nom affiche.
    const nom = destinataireDe(n, nommer);
    destinataires.set(nom, nom);
  }
  const trie = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label, 'fr');
  const filtres: ScreenFilterDef<EnvoiJournal>[] = [];
  if (statuts.has('echec') || statuts.has('abandonne')) {
    filtres.push({ id: 'envoi:echecs', label: 'Échecs', match: (n) => n.statut === 'echec' || n.statut === 'abandonne' });
  }
  for (const s of STATUTS_ENVOI) {
    if (statuts.has(s.id)) filtres.push({ id: `envoi:statut:${s.id}`, label: s.label, match: (n) => statutAffiche(n) === s.id });
  }
  filtres.push(...[...canaux].map((c) => ({ id: `envoi:canal:${c}`, label: libelleDeCanal(c), match: (n: EnvoiJournal) => n.canal === c })).sort(trie));
  filtres.push(...[...destinataires.keys()].map((nom) => ({ id: `envoi:a:${nom}`, label: `À ${nom}`, match: (n: EnvoiJournal) => destinataireDe(n, nommer) === nom })).sort(trie));
  return filtres;
}

/** Les trois stats de la porte (zone 2c), sur son jeu brut. */
export function statsDesEnvois(envois: readonly NotificationView[], tronque = false): ScreenStat[] {
  const n = envois.length;
  const echecs = envois.filter((e) => e.statut === 'echec' || e.statut === 'abandonne').length;
  const simules = envois.filter((e) => statutAffiche(e) === 'simule').length;
  return [
    { value: `${n}${tronque ? '+' : ''}`, label: n > 1 ? 'envois' : 'envoi' },
    { value: String(echecs), label: echecs > 1 ? 'échecs' : 'échec' },
    { value: String(simules), label: simules > 1 ? 'simulés' : 'simulé' },
  ];
}

/**
 * Ce que dit le geste « Rejouer » apres le passage de la file. Le moteur ne
 * rejoue qu'un echec DU (son attente croissante, 2 a 120 minutes, est passee) :
 * sans rien de du, la phrase donne l'heure de la prochaine tentative.
 */
export function bilanDeRejeu(sweeper: Record<string, number> | undefined, prochaine: Date | null, maintenant: Date = new Date()): string {
  const rejoues = sweeper?.['rejoues'] ?? 0;
  if (rejoues > 0) return `${rejoues} envoi${rejoues > 1 ? 's' : ''} rejoué${rejoues > 1 ? 's' : ''}`;
  if (prochaine && prochaine.getTime() > maintenant.getTime()) {
    const heure = new Intl.DateTimeFormat('fr-LU', { timeStyle: 'short' }).format(prochaine);
    return `Rien à rejouer avant ${heure}`;
  }
  return 'Rien à rejouer';
}
